import React, { useEffect, useContext, createContext } from 'react';
import useTypedPage from '@/Hooks/useTypedPage';
import useRoute from '@/Hooks/useRoute';
import { useMode } from '@/Contexts/ModeProvider';
import { useForm } from '@inertiajs/react';
import { router } from '@inertiajs/react';
import { InertiaFormProps } from '@inertiajs/react/types/useForm';

interface Props {
  conversation: App.Data.ConversationData;
  files: App.Data.FileData[];
  file?: App.Data.FileData;
}

export type SendForm = {
  message: string;
  mode?: number;
  preset?: number;
  file?: string;
  classification?: number | null;
};

export interface MessageContextProps {
  send: (event?: React.FormEvent<HTMLFormElement>) => void;
  sendDirect: (payload: SendForm) => void;
  sendToAssistant: (payload) => void;
  handleKeyPress: (event: React.KeyboardEvent) => void;
  processing: boolean;
  form: InertiaFormProps<SendForm>;
}

export const MessageContext = createContext<MessageContextProps | undefined>(
  undefined
);

export const useMessageProvider = () => {
  const context = useContext(MessageContext);
  if (!context) {
    throw new Error(
      `useMessageProvider must be used within an MessageProvider`
    );
  }
  return context;
};

export const MessageProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const route = useRoute();
  const { mode, preset } = useMode();
  const { file, conversation } = useTypedPage<Props>().props;

  const form = useForm<SendForm>('sendForm', {
    message: '',
    mode: mode?.id,
    preset: preset?.id,
    file: file?.id,
    classification: !file?.id ? 10 : null,
  });

  useEffect(() => {
    form.setData(prev => ({
      ...prev,
      mode: mode?.id,
      preset: preset?.id,
    }));
  }, [mode, preset]);

  function send(event?: React.FormEvent<HTMLFormElement>) {
    event?.preventDefault();
    if (!file) {
      form.transform((data: SendForm) => {
        delete data.preset;
        return { ...data, classification: 10 };
      });
    }

    form.post(route('conversations.send', [conversation]), {
      preserveState: false,
      preserveScroll: true,
      onBefore: () => {
        form.setData('message', '');
      },
    });
  }

  function sendDirect(payload: SendForm) {
    router.post(route('conversations.send', [conversation]), payload, {
      preserveState: false,
      preserveScroll: true,
    });
  }

  function sendToAssistant(payload) {
    router.post(
      route('conversations.debug-error', [conversation]),
      {
        mode: mode?.id,
        preset: preset?.id,
        ...payload,
      },
      {
        preserveState: false,
        preserveScroll: true,
      }
    );
  }

  function handleKeyPress(event: React.KeyboardEvent) {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      send();
    }
  }

  return (
    <MessageContext.Provider
      value={{
        send,
        sendDirect,
        sendToAssistant,
        handleKeyPress,
        processing: form.processing,
        form,
      }}
    >
      {children}
    </MessageContext.Provider>
  );
};

export default MessageProvider;
